import styled, { css } from 'styled-components';
import { riseIn } from '@shared/theme/animations';
import { fleetLeading, fleetType } from '@entities/fleet';

/**
 * Схема цвета для системных элементов (флажки, поле даты, списки): по теме
 * ОСНОВЫ, а не по теме системы — иначе при тёмной Windows и светлой ОСНОВЕ
 * флажки рисуются тёмными квадратами.
 */
export const colorScheme = css`
  color-scheme: ${({ theme }) => (theme.name === 'dark' ? 'dark' : 'light')};
`;

/** Контейнер раздела ПРЦ — от его ширины зависят раскладки (container queries). */
export const PrcRoot = styled.div`
  container: prc / inline-size;
  ${colorScheme}
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

/** Та же схема цвета внутри боковых панелей (они открываются поверх страницы). */
export const SchemeScope = styled.div`
  display: contents;
  ${colorScheme}
`;

/** Узкий контейнер: телефон и свёрнутая колонка. */
export const NARROW = '720px';

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
`;

export const Section = styled.section`
  padding-top: ${({ theme }) => theme.spacing(6)};

  &:first-child {
    padding-top: ${({ theme }) => theme.spacing(4)};
  }
`;

export const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  & > .aside {
    margin-left: auto;
  }

  @container prc (max-width: ${NARROW}) {
    & > .aside {
      margin-left: 0;
      flex: 1 1 100%;
    }
  }
`;

/** «01 Шины» — номер раздела моноширинным, как графа бланка (как в паспорте техники). */
export const SectionTitle = styled.h3`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin: 0;
  font-size: ${fleetType.lead};
  font-weight: 600;
  line-height: ${fleetLeading.tight};
  text-wrap: balance;

  & > .n {
    flex: none;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.small};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const SectionNote = styled.p`
  margin: 0;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const Lead = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  max-width: 76ch;
  font-size: ${fleetType.base};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const Stack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:empty {
    display: none;
  }
`;

/** Метки в строку: статус, ошибка, предупреждения. */
export const Chips = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};

  &:empty {
    display: none;
  }
`;

/** Ключевые цифры: плитки «подпись — значение», в узком контейнере — по две. */
export const StatGrid = styled.dl<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols ?? 4}, minmax(0, 1fr));
  gap: 1px;
  margin: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.border};
  overflow: hidden;

  & > div {
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }

  & dt {
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & dd {
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.primary};
    overflow-wrap: anywhere;
  }

  & dd.big {
    font-size: ${fleetType.title};
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  & dd .sub {
    display: block;
    margin-top: 2px;
    font-size: ${fleetType.small};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  /* В две колонки; нечётная последняя плитка — во всю ширину, без пустой клетки. */
  @container prc (max-width: ${NARROW}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > div:last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }

  @container drawer (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > div:last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }
`;

export const EmptyBox = styled.div`
  max-width: 680px;
  margin-top: ${({ theme }) => theme.spacing(6)};
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    text-wrap: balance;
  }

  & > p {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
  }

  @container prc (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(4)};
  }
`;

/** Полоса сохранения правок: прилипает к низу экрана, пока есть что сохранить. */
export const SaveBar = styled.div`
  position: sticky;
  bottom: ${({ theme }) => theme.spacing(3)};
  z-index: ${({ theme }) => theme.z.sticky};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
  animation: ${riseIn} ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  & > .status {
    flex: 1 1 240px;
    min-width: 0;
    margin: 0;
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.tight};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .status > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & > .buttons {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
  }

  @container prc (max-width: ${NARROW}) {
    & > .buttons {
      flex: 1 1 100%;
    }

    & > .buttons > button {
      flex: 1 1 160px;
    }
  }
`;

export const segmentButton = css`
  min-height: 32px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: ${fleetType.body};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.backgroundElevated};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
    box-shadow: ${({ theme }) => theme.shadow.xs};
  }

  /* Тень выбранного сегмента не должна гасить общее кольцо фокуса. */
  &:focus-visible {
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    cursor: default;
  }
`;

/**
 * Сегменты-переключатель (года, вариант, фильтры). На узком листе сегменты
 * переносятся строками — там они отдельные «таблетки»: перенесённая полоса
 * сегментов читалась бы как сломанная. Выбранная — инверсией, не только цветом.
 */
export const Segmented = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  @container prc (max-width: ${NARROW}) {
    display: flex;
    gap: ${({ theme }) => theme.spacing(1.5)};
    padding: 0;
    border: none;
    background: none;

    & > button {
      min-height: 36px;
      border: 1px solid ${({ theme }) => theme.colors.border};
      border-radius: ${({ theme }) => theme.radius.md};
      background: ${({ theme }) => theme.colors.backgroundElevated};
    }

    & > button[aria-pressed='true'] {
      border-color: ${({ theme }) => theme.colors.text.primary};
      background: ${({ theme }) => theme.colors.text.primary};
      color: ${({ theme }) => theme.colors.backgroundElevated};
      box-shadow: none;
    }

    & > button:focus-visible {
      box-shadow: ${({ theme }) => theme.shadow.focus};
    }
  }
`;

/** Года горизонта: на узком листе — ровная сетка, подпись «база» под годом. */
export const YearGroup = styled(Segmented)`
  & .base {
    font-weight: 500;
  }

  @container prc (max-width: ${NARROW}) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(48px, 1fr));
    width: 100%;

    & > button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 44px;
      padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(0.5)}`};
    }

    & .sep {
      display: none;
    }

    & .base {
      font-size: ${fleetType.micro};
      line-height: ${fleetLeading.tight};
    }
  }
`;

export const SegmentButton = styled.button`
  ${segmentButton}
`;

export const TopList = styled.ul`
  flex: 1 1 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};

  & > li {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => `0 ${theme.spacing(2)}`};
    padding: ${({ theme }) => `${theme.spacing(1)} 0`};
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  & .name {
    flex: 1 1 220px;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  & .nums {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & .nums > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const ReadValueBox = styled.span`
  display: block;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
`;
